import React, { useEffect, useState, useCallback } from "react";
import { Network, RefreshCw, ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { Card, Spinner } from "@/components/Kit";

const RPKI = {
  valid: { c: "#2E7D32", icon: ShieldCheck, label: "RPKI valid" },
  invalid: { c: "#A5300F", icon: ShieldAlert, label: "RPKI INVALID" },
  "unknown": { c: "#8A8175", icon: ShieldQuestion, label: "RPKI unknown" },
};

function RpkiChip({ status }) {
  const s = RPKI[status] || RPKI.unknown;
  const Icon = s.icon;
  return <span className="inline-flex items-center gap-1 text-[11px] font-bold" style={{ color: s.c }}><Icon className="w-3.5 h-3.5" />{s.label}</span>;
}

export default function Routing() {
  const { orgId, org } = useOrg();
  const { can } = useAuth();
  const canManage = can("monitor.watchers", "manage");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    const { data: watchers } = await http.get("/monitoring/watchers", { params: { org_id: orgId } });
    const bgp = watchers.filter((w) => w.watcher_type === "bgp");
    const withSnaps = await Promise.all(bgp.map(async (w) => {
      try {
        const { data: snaps } = await http.get(`/monitoring/watchers/${w.id}/snapshots`, { params: { org_id: orgId } });
        const cur = snaps.find((s) => s.is_current) || snaps[0];
        return { ...w, snapshot: cur };
      } catch { return { ...w, snapshot: null }; }
    }));
    setItems(withSnaps);
  }, [orgId]);
  useEffect(() => { setItems(null); }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const runNow = async (w) => {
    setBusy(w.id);
    try { await http.post(`/monitoring/watchers/${w.id}/run`, {}, { params: { org_id: orgId } }); toast.success("Route check ran"); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (items === null) return <div className="p-10 flex justify-center"><Spinner /></div>;

  return (
    <div className="p-6 max-w-[1500px] mx-auto fade-up" data-testid="routing-view">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#FFF5EB" }}><Network className="w-5 h-5 text-[#A5300F]" /></div>
        <div>
          <h1 className="font-display text-3xl font-bold text-[#A5300F]">BGP Routing &amp; RPKI</h1>
          <p className="text-sm text-[#8A8175]">{org?.name} · origin-ASN &amp; RPKI monitoring via RIPEstat + real-time RIS Live hijack detection.</p>
        </div>
      </div>

      <div className="grid gap-4 mt-6 md:grid-cols-2 xl:grid-cols-3">
        {items.map((w) => {
          const prefixes = w.snapshot?.payload?.prefixes || {};
          const entries = Object.entries(prefixes);
          return (
            <Card key={w.id} className="p-4" data-testid={`routing-card-${w.subject_ref}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-[#323232]">{w.subject_ref}</div>
                {canManage && <button onClick={() => runNow(w)} disabled={busy === w.id} data-testid={`routing-run-${w.subject_ref}`} className="p-1.5 text-[#8A8175] hover:text-[#A5300F]">{busy === w.id ? <Spinner /> : <RefreshCw className="w-4 h-4" />}</button>}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#B5AC9E] font-bold mt-0.5">
                {w.last_success_at ? `Checked ${new Date(w.last_success_at).toLocaleString("en-GB")}` : "Not yet checked"}
              </div>
              {entries.length === 0 ? (
                <div className="text-xs text-[#B5AC9E] mt-4 py-4 text-center">No route data captured yet. Run a check to populate.</div>
              ) : (
                <div className="mt-3 space-y-2.5">
                  {entries.map(([ip, p]) => (
                    <div key={ip} className="border border-[#F0E8DD] rounded-lg p-2.5 bg-[#FDFBF8]">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-[#5C554C]">{ip}</span>
                        <span className="font-mono text-[11px] font-bold text-[#A5300F]">{p.prefix || "unrouted"}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[11px] text-[#8A8175]">Origin {(p.origin_asns || []).map((a) => `AS${a}`).join(", ") || "—"}</span>
                        <div className="flex flex-col items-end gap-0.5">
                          {Object.entries(p.rpki || {}).map(([asn, st]) => <RpkiChip key={asn} status={st} />)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
        {items.length === 0 && <div className="col-span-full py-10 text-center text-[#B5AC9E]">No BGP watchers configured for this organisation.</div>}
      </div>
    </div>
  );
}
